import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ROLE_CHOICES, FUNDING_TYPE_CHOICES, COUNTRY_CHOICES, COUNTIES_BY_COUNTRY } from '../../utils/constants';
import LoadingSpinner from '../common/LoadingSpinner';

const ProfileForm = () => {
  const { user, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    company_name: '',
    roles: [],
    role_other_text: '',
    country: [],
    counties: [],
    funding_types_of_interest: [],
    funding_types_other_text: '',
    email_notifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEditingLocation, setIsEditingLocation] = useState(true);
  const [isEditingRoles, setIsEditingRoles] = useState(true);
  const [isEditingFundingTypes, setIsEditingFundingTypes] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendPasswordReset = useCallback(async (token) => {
    setChangingPassword(true);
    setError('');
    try {
      const result = token
        ? await forgotPassword(user.email, token)
        : await forgotPassword(user.email);
      if (result.user_id) {
        navigate('/reset-password', {
          state: { userId: result.user_id, email: user.email }
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset code. Please try again.');
    } finally {
      setChangingPassword(false);
      setShowTurnstile(false);
    }
  }, [user, forgotPassword, navigate]);

  const renderTurnstile = useCallback(() => {
    if (turnstileRef.current && window.turnstile && turnstileWidgetId.current === null) {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        size: 'normal',
        callback: (token) => {
          setTurnstileToken(token);
          sendPasswordReset(token);
        },
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => {
          setTurnstileToken('');
          setChangingPassword(false);
          setShowTurnstile(false);
          setError('Human verification failed. Please try again.');
        },
        'timeout-callback': () => {
          setTurnstileToken('');
          setChangingPassword(false);
          setShowTurnstile(false);
          setError('Human verification timed out. Please try again.');
        },
      });
    }
  }, [turnstileSiteKey, sendPasswordReset]);

  useEffect(() => {
    if (!showTurnstile || !turnstileSiteKey) return;

    // Reset widget ref so a fresh one renders
    if (turnstileWidgetId.current !== null && window.turnstile) {
      window.turnstile.remove(turnstileWidgetId.current);
      turnstileWidgetId.current = null;
    }

    if (window.turnstile) {
      renderTurnstile();
      return;
    }
    const interval = setInterval(() => {
      if (window.turnstile) {
        renderTurnstile();
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [showTurnstile, turnstileSiteKey, renderTurnstile]);

  const handleChangePassword = async () => {
    setError('');
    if (turnstileSiteKey) {
      // Show the captcha — it will auto-submit once completed
      setChangingPassword(true);
      setShowTurnstile(true);
    } else {
      // No turnstile configured, proceed directly
      await sendPasswordReset(null);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile/');
      console.log('Profile fetch response:', response.data);
      if (response.data.length > 0) {
        const profileData = response.data[0];
        console.log('Setting profile data:', profileData);
        const country = profileData.country || [];
        setProfile({
          company_name: profileData.company_name || '',
          roles: profileData.roles || [],
          role_other_text: profileData.role_other_text || '',
          country,
          counties: profileData.counties || [],
          funding_types_of_interest: profileData.funding_types_of_interest || [],
          funding_types_other_text: profileData.funding_types_other_text || '',
          email_notifications: profileData.email_notifications !== undefined ? profileData.email_notifications : true,
        });
        if (country.length > 0) setIsEditingLocation(false);
        if ((profileData.roles || []).length > 0) setIsEditingRoles(false);
        if ((profileData.funding_types_of_interest || []).length > 0) setIsEditingFundingTypes(false);
      } else {
        console.log('No profile found, using default values');
        setProfile(prev => ({
          ...prev,
          first_name: user?.first_name || '',
          last_name: user?.last_name || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleCountryToggle = (countryValue) => {
    setProfile(prev => {
      const isSelected = prev.country.includes(countryValue);
      const newCountry = isSelected
        ? prev.country.filter(c => c !== countryValue)
        : [...prev.country, countryValue];

      if (isSelected) {
        const countryEntry = COUNTIES_BY_COUNTRY.find(c => c.country === countryValue);
        const countiesToRemove = countryEntry ? countryEntry.counties : [];
        return {
          ...prev,
          country: newCountry,
          counties: prev.counties.filter(c => !countiesToRemove.includes(c)),
        };
      }

      return { ...prev, country: newCountry };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const errors = [];

      // Update user's first and last name
      try {
        await api.patch('/auth/user/', {
          first_name: profile.first_name,
          last_name: profile.last_name
        });
      } catch (userErr) {
        console.error('Error updating user:', userErr);
        if (userErr.response?.data) {
          const errorData = userErr.response.data;
          if (typeof errorData === 'object' && !Array.isArray(errorData)) {
            Object.entries(errorData).forEach(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              errors.push(message);
            });
          }
        }
      }

      // Prepare profile data (exclude first_name and last_name as they're user fields)
      const profileData = {
        company_name: profile.company_name,
        roles: profile.roles,
        role_other_text: profile.role_other_text,
        country: profile.country,
        counties: profile.counties,
        funding_types_of_interest: profile.funding_types_of_interest,
        funding_types_other_text: profile.funding_types_other_text,
        email_notifications: profile.email_notifications,
      };

      const response = await api.get('/profile/');
      console.log('Current profile check:', response.data);

      try {
        if (response.data.length > 0) {
          // Update existing profile
          await api.put(`/profile/${response.data[0].id}/`, profileData);
        } else {
          // Create new profile
          console.log('Creating new profile...');
          await api.post('/profile/', profileData);
        }
      } catch (profileErr) {
        console.error('Error updating profile:', profileErr);
        if (profileErr.response?.data) {
          const errorData = profileErr.response.data;
          if (typeof errorData === 'object' && !Array.isArray(errorData)) {
            Object.entries(errorData).forEach(([field, messages]) => {
              const message = Array.isArray(messages) ? messages[0] : messages;
              errors.push(message);
            });
          }
        }
      }

      // If there were any errors, display them
      if (errors.length > 0) {
        setError(errors.map(msg => `• ${msg}`).join('\n'));
        return;
      }

      setMessage('Profile updated successfully!');
      // Refetch profile to ensure UI is updated
      await fetchProfile();
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Profile</h1>

        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 whitespace-pre-line">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name</label>
                      <p className="mt-1 text-sm text-gray-900">{user?.first_name || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name</label>
                      <p className="mt-1 text-sm text-gray-900">{user?.last_name || 'Not set'}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                  Company / Farm Name
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={profile.company_name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your company or farm name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="mt-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ color: '#615DEC' }}
                  onMouseEnter={(e) => { if (!changingPassword) e.target.style.color = '#4c47c7'; }}
                  onMouseLeave={(e) => { e.target.style.color = '#615DEC'; }}
                >
                  {changingPassword ? 'Sending reset code...' : 'Change password'}
                </button>
                {showTurnstile && <div ref={turnstileRef} className="mt-2" />}
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">What best describes you?</h2>
              {!isEditingRoles && profile.roles.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsEditingRoles(true)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </button>
              )}
            </div>

            {isEditingRoles ? (
              <>
                <p className="text-sm text-gray-600 mb-4">Select all that apply</p>
                <div className="space-y-2">
                  {ROLE_CHOICES.map((role) => (
                    <label key={role.value} className="flex items-center">
                      <input
                        type="checkbox"
                        value={role.value}
                        checked={profile.roles.includes(role.value)}
                        onChange={() => handleCheckboxChange('roles', role.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{role.label}</span>
                    </label>
                  ))}
                </div>

                {profile.roles.includes('other') && (
                  <div className="mt-3">
                    <label htmlFor="role_other_text" className="block text-sm font-medium text-gray-700">
                      Please specify
                    </label>
                    <input
                      type="text"
                      id="role_other_text"
                      name="role_other_text"
                      value={profile.role_other_text}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Describe your role"
                    />
                  </div>
                )}

                {profile.roles.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsEditingRoles(false)}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Done
                  </button>
                )}
              </>
            ) : (
              profile.roles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.roles.map(r => {
                    const label = r === 'other' && profile.role_other_text
                      ? profile.role_other_text
                      : ROLE_CHOICES.find(c => c.value === r)?.label || r;
                    return (
                      <span key={r} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {label}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No roles set</p>
              )
            )}
          </div>

          {/* Location */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Location</h2>
              {!isEditingLocation && profile.country.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsEditingLocation(true)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </button>
              )}
            </div>

            {isEditingLocation ? (
              <>
                <p className="text-sm text-gray-600 mb-3">Select countries</p>
                <div className="flex flex-wrap gap-4 mb-4">
                  {COUNTRY_CHOICES.map((option) => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={profile.country.includes(option.value)}
                        onChange={() => handleCountryToggle(option.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>

                {profile.country.map(countryValue => {
                  const countryEntry = COUNTIES_BY_COUNTRY.find(c => c.country === countryValue);
                  if (!countryEntry) return null;
                  return (
                    <div key={countryValue} className="mb-4">
                      <p className="text-sm font-medium text-gray-600 mb-2">{countryEntry.country} — Counties</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                        {countryEntry.counties.map((county) => (
                          <label key={county} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={profile.counties.includes(county)}
                              onChange={() => handleCheckboxChange('counties', county)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{county}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {profile.country.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsEditingLocation(false)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Done
                  </button>
                )}
              </>
            ) : (
              profile.country.length > 0 ? (
                <div>
                  <div className="flex flex-wrap gap-2">
                    {profile.country.map(c => (
                      <span key={c} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {c}
                      </span>
                    ))}
                  </div>
                  {profile.counties.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {profile.counties.map(c => (
                        <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No location set</p>
              )
            )}
          </div>

          {/* Funding Type Selection */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">What types of funding are you interested in?</h2>
              {!isEditingFundingTypes && profile.funding_types_of_interest.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsEditingFundingTypes(true)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </button>
              )}
            </div>

            {isEditingFundingTypes ? (
              <>
                <p className="text-sm text-gray-600 mb-4">Select all that apply</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {FUNDING_TYPE_CHOICES.map((type) => (
                    <label key={type.value} className="flex items-center">
                      <input
                        type="checkbox"
                        value={type.value}
                        checked={profile.funding_types_of_interest.includes(type.value)}
                        onChange={() => handleCheckboxChange('funding_types_of_interest', type.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>

                {profile.funding_types_of_interest.includes('other') && (
                  <div className="mt-3">
                    <label htmlFor="funding_types_other_text" className="block text-sm font-medium text-gray-700">
                      Please specify
                    </label>
                    <input
                      type="text"
                      id="funding_types_other_text"
                      name="funding_types_other_text"
                      value={profile.funding_types_other_text}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Describe the funding type"
                    />
                  </div>
                )}

                {profile.funding_types_of_interest.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsEditingFundingTypes(false)}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Done
                  </button>
                )}
              </>
            ) : (
              profile.funding_types_of_interest.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profile.funding_types_of_interest.map(ft => {
                    const label = ft === 'other' && profile.funding_types_other_text
                      ? profile.funding_types_other_text
                      : FUNDING_TYPE_CHOICES.find(c => c.value === ft)?.label || ft;
                    return (
                      <span key={ft} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {label}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No funding types set</p>
              )
            )}
          </div>

          {/* Notification Preferences */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h2>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="email_notifications"
                name="email_notifications"
                checked={profile.email_notifications}
                onChange={(e) => setProfile(prev => ({
                  ...prev,
                  email_notifications: e.target.checked
                }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="email_notifications" className="ml-3 block text-sm font-medium text-gray-700">
                Send me email notifications for new funding opportunities that match my preferences
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileForm;
